const debug = process.env.REACT_APP_DEBUG === "true";

const TEST_BACKEND_ORIGIN = "http://209.97.189.111:8000";
const TEST_FRONTEND_ORIGIN = "http://209.97.189.111:3000";

const DEFAULT_PATHWAYS_ORIGIN = "https://pathwayssoftware.co.uk";
const ALPHA_PATHWAYS_ORIGIN = "https://alpha.pathwayssoftware.co.uk";
const BETA_PATHWAYS_ORIGIN = "https://beta.pathwayssoftware.co.uk";

const RESOLVED_DOMAIN_KEY = "pathways_resolved_backend_origin";
const DOMAIN_REDIRECT_CHECK_KEY = "pathways_domain_redirect_checking";

export type OrganisationAccountTypeId = number | string | null | undefined;

let cachedBackendOrigin: string | null = null;

function withTrailingSlash(url: string) {
  return url.endsWith("/") ? url : `${url}/`;
}

function withoutTrailingSlash(url: string) {
  return url.endsWith("/") ? url.slice(0, -1) : url;
}

function canUseLocalStorage() {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof sessionStorage !== "undefined";
}

/**
 * Converts account type values into a predictable number.
 *
 * This keeps the resolver safe if the backend returns:
 *   1
 *   "1"
 *   2
 *   "2"
 *
 * If the account type is missing, invalid, or unknown, this returns null.
 */
export function normaliseAccountTypeId(
  accountType?: OrganisationAccountTypeId
): number | null {
  if (accountType === null || accountType === undefined) {
    return null;
  }

  const parsed = Number(accountType);

  return Number.isFinite(parsed) ? parsed : null;
}

export function isDebugMode() {
  return debug;
}

export function getCurrentPublishedOrigin() {
  if (typeof window === "undefined") {
    return DEFAULT_PATHWAYS_ORIGIN;
  }

  const hostname = window.location.hostname;

  if (hostname === "alpha.pathwayssoftware.co.uk") {
    return ALPHA_PATHWAYS_ORIGIN;
  }

  if (hostname === "beta.pathwayssoftware.co.uk") {
    return BETA_PATHWAYS_ORIGIN;
  }

  return DEFAULT_PATHWAYS_ORIGIN;
}

export function getInitialBackendOrigin() {
  if (debug) {
    return TEST_BACKEND_ORIGIN;
  }

  return getCurrentPublishedOrigin();
}

export function getInitialFrontendOrigin() {
  if (debug) {
    return TEST_FRONTEND_ORIGIN;
  }

  return getCurrentPublishedOrigin();
}

/**
 * Initial/public backend URL.
 *
 * Debug:
 *   http://209.97.189.111:8000/
 *
 * Published:
 *   https://pathwayssoftware.co.uk/api/
 *   https://alpha.pathwayssoftware.co.uk/api/
 *   https://beta.pathwayssoftware.co.uk/api/
 */
export function getInitialBaseURLBackend() {
  if (debug) {
    return withTrailingSlash(TEST_BACKEND_ORIGIN);
  }

  return `${getInitialBackendOrigin()}/api/`;
}

export function resolveOriginFromAccountType(
  accountType?: OrganisationAccountTypeId
) {
  if (debug) {
    return TEST_BACKEND_ORIGIN;
  }

  const accountTypeId = normaliseAccountTypeId(accountType);

  if (accountTypeId === 1) {
    return ALPHA_PATHWAYS_ORIGIN;
  }

  if (accountTypeId === 2) {
    return BETA_PATHWAYS_ORIGIN;
  }

  return DEFAULT_PATHWAYS_ORIGIN;
}

export function getFrontendOriginForAccountType(
  accountType?: OrganisationAccountTypeId
) {
  if (debug) {
    return TEST_FRONTEND_ORIGIN;
  }

  const accountTypeId = normaliseAccountTypeId(accountType);

  if (accountTypeId === 1) {
    return ALPHA_PATHWAYS_ORIGIN;
  }

  if (accountTypeId === 2) {
    return BETA_PATHWAYS_ORIGIN;
  }

  return DEFAULT_PATHWAYS_ORIGIN;
}

export function storeResolvedBackendOrigin(origin: string) {
  const cleanOrigin = withoutTrailingSlash(origin);

  cachedBackendOrigin = cleanOrigin;

  if (canUseLocalStorage()) {
    localStorage.setItem(RESOLVED_DOMAIN_KEY, cleanOrigin);
  }
}

export function storeResolvedBackendOriginForAccountType(
  accountType?: OrganisationAccountTypeId
) {
  const origin = resolveOriginFromAccountType(accountType);
  storeResolvedBackendOrigin(origin);
  return origin;
}

export function getBackendOrigin() {
  if (debug) {
    return TEST_BACKEND_ORIGIN;
  }

  if (cachedBackendOrigin) {
    return cachedBackendOrigin;
  }

  if (canUseLocalStorage()) {
    const stored = localStorage.getItem(RESOLVED_DOMAIN_KEY);

    if (stored) {
      cachedBackendOrigin = withoutTrailingSlash(stored);
      return cachedBackendOrigin;
    }
  }

  /**
   * If no resolved backend has been stored yet, use the current frontend's
   * matching backend.
   */
  return getInitialBackendOrigin();
}

/**
 * Normal API backend URL.
 *
 * Debug:
 *   http://209.97.189.111:8000/
 *
 * Published:
 *   https://pathwayssoftware.co.uk/api/
 *   https://alpha.pathwayssoftware.co.uk/api/
 *   https://beta.pathwayssoftware.co.uk/api/
 */
export function getBaseURLBackend() {
  if (debug) {
    return withTrailingSlash(TEST_BACKEND_ORIGIN);
  }

  return `${getBackendOrigin()}/api/`;
}

export function getBaseURLFrontend() {
  if (debug) {
    return TEST_FRONTEND_ORIGIN;
  }

  return getCurrentPublishedOrigin();
}

export function clearResolvedBackendOrigin() {
  cachedBackendOrigin = null;

  if (canUseLocalStorage()) {
    localStorage.removeItem(RESOLVED_DOMAIN_KEY);
  }
}

export function isOnCorrectFrontendForAccountType(
  accountType?: OrganisationAccountTypeId
) {
  if (debug || typeof window === "undefined") {
    return true;
  }

  const expectedOrigin = withoutTrailingSlash(
    getFrontendOriginForAccountType(accountType)
  );

  const currentOrigin = withoutTrailingSlash(window.location.origin);

  return currentOrigin === expectedOrigin;
}

export function redirectToFrontendForAccountType(
  accountType?: OrganisationAccountTypeId,
  path = "/"
) {
  if (debug || typeof window === "undefined") {
    return false;
  }

  const expectedOrigin = withoutTrailingSlash(
    getFrontendOriginForAccountType(accountType)
  );

  const currentOrigin = withoutTrailingSlash(window.location.origin);

  if (currentOrigin !== expectedOrigin) {
    window.location.href = `${expectedOrigin}${path}`;
    return true;
  }

  return false;
}

export function redirectToCorrectFrontendForCurrentPath(
  accountType?: OrganisationAccountTypeId
) {
  if (typeof window === "undefined") {
    return false;
  }

  const path = `${window.location.pathname}${window.location.search}`;

  return redirectToFrontendForAccountType(accountType, path);
}

export function beginDomainRedirectCheck() {
  if (canUseSessionStorage()) {
    sessionStorage.setItem(DOMAIN_REDIRECT_CHECK_KEY, "true");
  }
}

export function endDomainRedirectCheck() {
  if (canUseSessionStorage()) {
    sessionStorage.removeItem(DOMAIN_REDIRECT_CHECK_KEY);
  }
}

export function isDomainRedirectCheckRunning() {
  if (!canUseSessionStorage()) {
    return false;
  }

  return sessionStorage.getItem(DOMAIN_REDIRECT_CHECK_KEY) === "true";
}